import { useMemo } from 'react'
import {
  CollectedDataStatus,
  CollectedDataStepType,
  ContractorStepExpirationStatus,
} from 'src/constants/collectedData'
import { CollectedData } from 'src/interfaces/collectedData'
import { ContractorDetails } from 'src/interfaces/contractor'
import { isStepRecollecting } from 'src/utils/collectedData'

export function getContractorStepItemState(
  params: ContractorStepItemStateParams,
): ContractorStepItemState {
  const { collectedData, contractorDetails } = params

  const stepCanBeRecollected =
    !!contractorDetails.collectDataTemplate?.collectDataTemplateSteps?.some((template) => {
      const hasExactMatchInTemplate =
        template.label === collectedData.collectDataStep.label &&
        template.type === collectedData.collectDataStep.type
      const hasRecollectOptionForSameStepType =
        template.type === collectedData.collectDataStep.type &&
        collectedData.collectDataStep.recollectSameTypeSteps

      return hasExactMatchInTemplate || hasRecollectOptionForSameStepType
    })

  const isBGCStepInProgress =
    collectedData.collectDataStep.type === CollectedDataStepType.BackgroundCheck &&
    !!collectedData?.dataStatus
  const isMvrStepInProgress =
    collectedData.collectDataStep.type === CollectedDataStepType.MvrUpload &&
    !!collectedData?.dataStatus
  const isLmsTrainingStepInProgress =
    collectedData.collectDataStep.type === CollectedDataStepType.LmsTraining &&
    !!collectedData.documents.length

  const stepStatus =
    (isBGCStepInProgress || isMvrStepInProgress || isLmsTrainingStepInProgress) &&
    collectedData.status === CollectedDataStatus.Created
      ? CollectedDataStatus.InProgress
      : collectedData.status

  const expiresAt = collectedData?.expiresAt || undefined
  const expirationStatus = collectedData.expirationStatus ?? null
  const expiresSoon = expirationStatus === ContractorStepExpirationStatus.SoonToExpire
  const isExpired = expirationStatus === ContractorStepExpirationStatus.Expired

  return {
    stepCanBeRecollected,
    stepStatus,
    isRecollecting: isStepRecollecting(collectedData),
    expiresAt,
    expiresSoon,
    isExpired,
  }
}

export interface ContractorStepItemStateParams {
  collectedData: CollectedData
  contractorDetails: ContractorDetails
  isRequestApprove?: boolean
  isSubcontractor?: boolean
}

export interface ContractorStepItemState {
  stepCanBeRecollected: boolean
  stepStatus: CollectedDataStatus
  isRecollecting: boolean
  expiresAt: string | undefined
  expiresSoon: boolean
  isExpired: boolean
}

export function useContractorStepItemState(
  params: ContractorStepItemStateParams,
): ContractorStepItemState {
  const { collectedData, contractorDetails, isRequestApprove, isSubcontractor } = params

  return useMemo(
    () => getContractorStepItemState(params),
    [collectedData, contractorDetails, isRequestApprove, isSubcontractor],
  )
}
